import React from 'react';
import { AlertTriangle, CheckCircle, ExternalLink, Copy } from 'lucide-react';
import { Button } from './ui/Button';
import { useTranslation } from '../lib/i18n';

export const PaymentSetupGuide: React.FC = () => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState<string | null>(null);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const envVariables = [
    {
      key: 'STRIPE_SECRET_KEY',
      description: t('paymentSetup.stripeSecretKeyDesc'),
      example: 'sk_test_...'
    },
    {
      key: 'STRIPE_WEBHOOK_SECRET',
      description: t('paymentSetup.webhookSecretDesc'),
      example: 'whsec_...'
    }
  ];

  const setupSteps = [
    {
      title: t('paymentSetup.createStripeAccount'),
      description: t('paymentSetup.createStripeDesc'),
      status: 'required'
    },
    {
      title: t('paymentSetup.configureEnvVars'),
      description: t('paymentSetup.configureEnvDesc'),
      status: 'required'
    },
    {
      title: t('paymentSetup.deployEdgeFunctions'),
      description: t('paymentSetup.deployEdgeDesc'),
      status: 'required'
    },
    {
      title: t('paymentSetup.setupWebhooks'),
      description: t('paymentSetup.setupWebhooksDesc'),
      status: 'required'
    },
    {
      title: t('paymentSetup.testPayments'),
      description: t('paymentSetup.testPaymentsDesc'),
      status: 'recommended'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mt-1" />
          <div>
            <h2 className="text-xl font-bold text-yellow-800 dark:text-yellow-200 mb-2">
              {t('paymentSetup.setupRequired')}
            </h2>
            <p className="text-yellow-700 dark:text-yellow-300">
              {t('paymentSetup.notConfigured')}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4">{t('paymentSetup.setupSteps')}</h3>
        <div className="space-y-4">
          {setupSteps.map((step, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                step.status === 'required' 
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400'
                  : 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400'
              }`}>
                {index + 1}
              </div>
              <div className="flex-1">
                <h4 className="font-medium">{step.title}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">{step.description}</p>
                {step.status === 'required' && (
                  <span className="inline-block mt-1 px-2 py-1 bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 text-xs rounded">
                    {t('paymentSetup.required')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4">{t('paymentSetup.envVariables')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          {t('paymentSetup.addEnvVars')}
        </p>
        <div className="space-y-4">
          {envVariables.map((env) => (
            <div key={env.key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <code className="font-mono text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {env.key}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(env.key, env.key)}
                >
                  {copied === env.key ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{env.description}</p>
              <code className="text-xs text-gray-500 dark:text-gray-400">{env.example}</code>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4">{t('paymentSetup.usefulLinks')}</h3>
        <div className="space-y-3">
          <a
            href="https://stripe.com/docs/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            <span>{t('paymentSetup.stripeApiKeysDocs')}</span>
          </a>
          <a
            href="https://supabase.com/docs/guides/functions/secrets"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            <span>{t('paymentSetup.supabaseEdgeSecrets')}</span>
          </a>
          <a
            href="https://stripe.com/docs/webhooks"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            <span>{t('paymentSetup.stripeWebhooksGuide')}</span>
          </a>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <h3 className="text-lg font-bold text-blue-800 dark:text-blue-200 mb-2">
          {t('paymentSetup.needHelp')}
        </h3>
        <p className="text-blue-700 dark:text-blue-300 text-sm">
          {t('paymentSetup.needHelpDesc')}
        </p>
      </div>
    </div>
  );
};